/**
 * FarmerContext — verified-farmer identity, scoped to a protected subtree.
 *
 * Populated by <FarmerContextRoute> (the identity middleware) and read
 * by inner pages via useFarmer(). The middleware guarantees the value
 * is fully populated by the time children render, so useFarmer() never
 * returns null — calling it outside the provider throws to fail loudly.
 *
 * Identity ONLY. Resource-scoped data (soil cards, passbook rows,
 * scheme submission state, …) is the job of per-resource hooks in
 * src/rewamp/hooks/resource/.
 *
 * See .agent/rewamp/cta/middleware-refactor-plan.md §1.1, §1.3.
 */
import React, { createContext, useContext } from "react";
import PropTypes from "prop-types";

// categoryTypes whose OTP response is Individual + BankDetails shaped (vs.
// a resource-flavoured payload like FARMER_PASSBOOK_GET/SOIL_CARD_STATUS_GET).
// SCHEME_APPLY_PROFILE_GET is scheme-apply's own identity categoryType —
// same response shape as PROFILE_GET, distinct wire value so the backend
// can tell scheme-apply's identity check apart from grievance-redressal's.
const PROFILE_IDENTITY_CATEGORY_TYPES = ["PROFILE_GET", "SCHEME_APPLY_PROFILE_GET"];
export const isProfileIdentityCategoryType = (categoryType) =>
  PROFILE_IDENTITY_CATEGORY_TYPES.includes(categoryType);

/**
 * @typedef {"AGENT" | "CITIZEN"} FarmerRole
 *
 * @typedef {object} Identifier
 * @property {string} identifierType  e.g. "DBTID", "AADHAAR"
 * @property {string} identifierId
 *
 * @typedef {object} Address
 * @property {string} [district]
 * @property {string} [block]
 * @property {string} [panchayat]
 * @property {string} [village]
 *
 * @typedef {object} Farmer
 * @property {string} dbtId                       13-digit DBT registration number.
 * @property {string} name                        Farmer's given name (always a string; "" if missing).
 * @property {string} [fatherHusbandName]
 * @property {string} [gender]
 * @property {string} [dateOfBirth]
 * @property {string} [individualType]
 * @property {string} [mobileNumber]
 * @property {Address[]} [address]
 * @property {Identifier[]} [identifiers]
 * @property {string} [aadharNumber]
 * @property {string} [individualCast]
 * @property {object} individual                  Raw Individual node from the
 *                                                server response — escape hatch
 *                                                for downstream code that reads
 *                                                fields we haven't normalised yet.
 * @property {object|null} [bankDetails]          Raw BankDetails node
 *                                                (bankName / accountNumber /
 *                                                ifscCode), sibling of Individual
 *                                                in the PROFILE_GET response. The
 *                                                scheme-apply form needs it; null
 *                                                when the response omits it or for
 *                                                resource categoryTypes.
 *
 * @typedef {object} FarmerContextValue
 * @property {FarmerRole} role
 * @property {Farmer} farmer                      Non-null inside the provider.
 * @property {string|null} [resourceTokenId]      Short-lived backend token
 *                                                surfaced when the middleware's
 *                                                categoryType is a resource
 *                                                type (e.g. FARMER_PASSBOOK_GET).
 *                                                Null for CITIZEN paths and for
 *                                                PROFILE_GET (identity-only)
 *                                                routes. Resource hooks read
 *                                                this and skip their inline OTP
 *                                                when present.
 * @property {object|null} [resourcePayload]      The full `proxyResponse` body
 *                                                from a resource-categoryType
 *                                                OTP exchange. Lets hooks pluck
 *                                                first-page data (e.g.
 *                                                SoilTestAnalysisStatuses,
 *                                                passbook Data) without a
 *                                                redundant follow-up call.
 *                                                Null on PROFILE_GET and CITIZEN.
 * @property {() => Promise<void>} refresh        Re-run identity load (CITIZEN
 *                                                = re-fetch profile, AGENT =
 *                                                re-render OTP verifier).
 * @property {(reason?: string) => void} invalidate
 *                                                Mark identity stale. Middleware
 *                                                re-renders its loader/verifier.
 */

/** @type {React.Context<FarmerContextValue | null>} */
const FarmerContextInternal = createContext(null);
FarmerContextInternal.displayName = "FarmerContext";

/**
 * Provider — used by FarmerContextRoute. Inner pages should NOT
 * instantiate this directly; that would skip the role-aware loader.
 */
export const FarmerProvider = ({ value, children }) => (
  <FarmerContextInternal.Provider value={value}>
    {children}
  </FarmerContextInternal.Provider>
);

FarmerProvider.propTypes = {
  value: PropTypes.object.isRequired,
  children: PropTypes.node,
};

/**
 * Read the verified farmer from context. Throws if used outside a
 * FarmerContextRoute — that's almost always a wiring bug.
 *
 * @returns {FarmerContextValue}
 */
export const useFarmer = () => {
  const value = useContext(FarmerContextInternal);
  if (value == null) {
    throw new Error(
      "useFarmer() called outside <FarmerContextRoute>. " +
        "Wrap the route with FarmerContextRoute before consuming farmer identity.",
    );
  }
  return value;
};

/**
 * Map a raw Individual node (from /agent/v1/entity/_search proxyResponse
 * or /farmer-profile data) into our normalised Farmer shape.
 *
 * Mirrors the open-coded mapping in GrmCreateForm.js:142-172 that this
 * refactor centralises. Returning empty-string defaults instead of " - "
 * because UI fallbacks belong in the view layer, not the model.
 *
 * @param {object | null | undefined} individual  Raw Individual node.
 * @returns {Farmer | null}  Null only when the node is absent/non-object;
 *                           non-DBT individuals return a Farmer with dbtId "".
 */
export const normaliseIndividual = (individual) => {
  if (!individual || typeof individual !== "object") return null;

  const identifiers = Array.isArray(individual.identifiers)
    ? individual.identifiers
    : [];
  const findIdentifier = (type) =>
    identifiers.find((i) => i && i.identifierType === type)?.identifierId ?? "";

  // Non-DBT farmers are valid records, not errors. They reach the view-only
  // pages (Passbook is userUuid-keyed; Soil renders an empty "No Data"
  // state) and are stopped only at DBT-requiring ACTIONS — grievance submit,
  // scheme apply — via <NonDbtNotice>. This mirrors dfs-mobile-app's
  // per-flow gating instead of the old blanket middleware block. `dbtId` is
  // "" for non-DBT; downstream action hooks/components MUST guard on it.
  // (The no-node case is already handled by the early return above.)
  const dbtId = findIdentifier("DBTID");

  const address = Array.isArray(individual.address) ? individual.address : [];

  return {
    dbtId,
    name: individual?.name?.givenName ?? "",
    fatherHusbandName: individual?.fatherHusbandName ?? "",
    gender: individual?.gender ?? "",
    dateOfBirth: individual?.dateOfBirth ?? "",
    individualType: individual?.individualType ?? "",
    mobileNumber: individual?.mobileNumber ?? "",
    address,
    identifiers,
    aadharNumber: findIdentifier("AADHAAR"),
    individualCast: individual?.individualCast ?? "",
    individual,
  };
};

/**
 * Build a minimal Farmer from a resource-categoryType proxy response.
 *
 * PROFILE_GET responses carry a full Individual node — normaliseIndividual
 * handles those. Resource categoryTypes (FARMER_PASSBOOK_GET,
 * SOIL_CARD_STATUS_GET, …) instead return resource-flavoured payloads
 * (Data, Address, SoilTestAnalysisStatuses, tokenId, …) WITHOUT
 * Individual. We still need a Farmer in FarmerContext so downstream
 * hooks/components can read `farmer.dbtId` etc. without branching on
 * the route's categoryType, so synthesise one from:
 *   - the dbtId the caller sent (always present — it's the request key)
 *   - Address fields, when the resource shape includes them
 *   - FarmerName / FatherName / MobileNo / AadharCard when present in
 *     the first record (soil-card responses surface these per-row)
 *
 * The raw proxyResponse is preserved under `.individual` for any
 * back-compat reads — but consumers shouldn't rely on it carrying an
 * Individual-shaped node when categoryType isn't PROFILE_GET.
 *
 * @param {object} proxyResponse Raw response.data.proxyResponse.
 * @param {string} dbtId         13-digit DBT id from the OTP request.
 * @returns {Farmer | null}      Null only when dbtId is missing.
 */
export const synthesiseFarmerFromResource = (proxyResponse, dbtId) => {
  if (!dbtId) return null;
  const proxy = proxyResponse && typeof proxyResponse === "object" ? proxyResponse : {};

  // Address (passbook responses): the resource shape has lowercase
  // LG fields (districtlg/blocklg/...) plus the full names. Match
  // both naming conventions so downstream code doesn't have to.
  const addr = proxy.Address;
  const address = addr
    ? [
        {
          district: addr.district ?? "",
          block: addr.block ?? "",
          panchayat: addr.panchayat ?? "",
          village: addr.village ?? "",
          districtLG: addr.districtlg ?? addr.districtLG ?? "",
          blockLG: addr.blocklg ?? addr.blockLG ?? "",
          panchayatLG: addr.panchayatlg ?? addr.panchayatLG ?? "",
          villageLG: addr.villagelg ?? addr.villageLG ?? "",
        },
      ]
    : [];

  // Soil-card responses carry farmer attrs on the first card row.
  const firstSoilRow = Array.isArray(proxy.SoilTestAnalysisStatuses)
    ? proxy.SoilTestAnalysisStatuses[0]
    : null;

  return {
    dbtId,
    name: firstSoilRow?.FarmerName ?? "",
    fatherHusbandName: firstSoilRow?.FatherName ?? "",
    gender: "",
    dateOfBirth: "",
    individualType: "",
    mobileNumber: firstSoilRow?.MobileNo ?? "",
    address,
    identifiers: [{ identifierType: "DBTID", identifierId: dbtId }],
    aadharNumber: firstSoilRow?.AadharCard ?? "",
    individualCast: "",
    // Preserve the raw response under `individual` for any
    // back-compat readers; note it is NOT an Individual node for
    // resource categoryTypes.
    individual: proxy,
  };
};

export const FarmerContext = FarmerContextInternal;
